import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import {
  setSdkParams,
  setIsMobile,
  setCustomStyles,
  setPreviewConfig,
  setIsDemo
} from 'redux/actions/global';
import { isSdkSelector } from 'redux/selectors/global';
import {
  PREVIEW_POSSIBLE_VIEWS,
  PREVIEW_REDIRECT_URLS
} from 'components/containers/Preview/consts';
import { isIframe } from 'utilities/global';
import { ICategory } from 'models';
import API from 'services/api';
import { sendParentMessage } from 'services/iframeService';

interface IPreviewToken {
  type: string;
  env: string;
  token: string;
  is_demo?: boolean;
}

const MESSAGE_PREFIX = 'PREVIEW';

export const OUTGOING_MESSAGES = {
  INITIALIZE: `${MESSAGE_PREFIX}_INITIALIZE`,
  ON_LOAD: `${MESSAGE_PREFIX}_ON_LOAD`,
  AVAILABLE_CATEGORIES: `${MESSAGE_PREFIX}_AVAILABLE_CATEGORIES`,
  ON_SELECT_CATEGORY: `${MESSAGE_PREFIX}_ON_SELECT_CATEGORY`,
  ON_SELECT_CARD_FIELD: `${MESSAGE_PREFIX}_ON_SELECT_CARD_FIELD`
};

export const PREVIEW_INCOMING_MESSAGES = {
  INITIALIZE: `${MESSAGE_PREFIX}_INITIALIZE`,
  PARAMS: `${MESSAGE_PREFIX}_PARAMS`,
  SET_CARD_DETAILS: `${MESSAGE_PREFIX}_SET_CARD_DETAILS`
};

const useListenToMessages = (cb: (this: Window, ev: MessageEvent<any>) => any) => {
  useEffect(() => {
    window.addEventListener('message', cb);

    return () => {
      window.removeEventListener('message', cb);
    };
  }, []);
};

export const sendPreviewLoaded = () => {
  sendParentMessage(OUTGOING_MESSAGES.ON_LOAD);
};

export const sendPreviewAvailableCategories = (categories: ICategory[] = []) => {
  const categoriesAsString = categories.map(category => category.category || '');

  sendParentMessage(OUTGOING_MESSAGES.AVAILABLE_CATEGORIES, categoriesAsString);
};

export const sendPreviewCategoryChange = (category: string) => {
  sendParentMessage(OUTGOING_MESSAGES.ON_SELECT_CATEGORY, { category });
};

export const sendPreviewCardFieldFocus = (cardField: string) => {
  sendParentMessage(OUTGOING_MESSAGES.ON_SELECT_CARD_FIELD, { cardField });
};

const HostedPagePreview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isInIframe = isIframe();

  const isSdk = useSelector(isSdkSelector);

  const [redirectUrl, setRedirectUrl] = useState('');

  const handleMessage = async (event: Record<string, any>) => {
    const { type, data } = event.data || {};
    if (type === PREVIEW_INCOMING_MESSAGES.INITIALIZE) {
      await handlePreviewParamsMessage(data);
    }
  };

  useListenToMessages(handleMessage);

  const initUrl = async (
    featureType: keyof typeof PREVIEW_REDIRECT_URLS,
    isDemo: boolean
  ) => {
    const baseUrl = PREVIEW_REDIRECT_URLS[featureType];
    const response = await API.get(`hosted/config/preview_tokens?type=${featureType}`);
    const {
      data: { data: previewTokens }
    } = response;
    const { token } =
      previewTokens.find(
        (pt: IPreviewToken) => pt.type === featureType && (!isDemo || pt.is_demo)
      ) || {};

    const redirectUrl = `${baseUrl}?token=${token}`;
    setRedirectUrl(redirectUrl);
  };

  const initView = (view: string) => {
    if (view === PREVIEW_POSSIBLE_VIEWS.sdk) return dispatch(setSdkParams({}, true));
    if (view === PREVIEW_POSSIBLE_VIEWS.mobile) return dispatch(setIsMobile(true));
  };

  const setPreviewZoom = (zoom: string) => {
    document.body.style.setProperty('zoom', zoom || '1');
  };

  const setAppHeights = () => {
    const rootElement: HTMLElement | null = document.body.querySelector('#root');
    document.documentElement.style.height = '100%';
    document.body.style.height = '100%';
    if (rootElement) rootElement.style.height = '100%';
  };

  const setCustomVariables = (params: Record<string, any>) => {
    const {
      buttonColor,
      ctaText,
      logo,
      categories,
      view,
      customStyles = {},
      reusablePage,
      checkoutPage,
      alias,
      language,
      previewApplePay,
      previewGooglePay,
      isDemo
    } = params || {};
    const previewConfig = {
      btnColor: buttonColor,
      ctaText,
      view,
      logo,
      categories,
      reusablePage,
      checkoutPage,
      alias,
      language,
      previewApplePay,
      previewGooglePay,
      isDemo
    };

    if (customStyles && Object.keys(customStyles).length && isSdk) {
      dispatch(setCustomStyles(customStyles));
    }

    dispatch(setPreviewConfig(previewConfig));
  };
  const handlePreviewParamsMessage = async (data: Record<string, any>) => {
    const { featureType, view, zoom, isDemo, ...rest } = data;
    const customVariables = {
      ...rest,
      isDemo,
      view
    };

    setAppHeights();
    await initUrl(featureType, isDemo);
    initView(view);
    setPreviewZoom(zoom);
    setCustomVariables(customVariables);
    dispatch(setIsDemo(isDemo));
  };

  useEffect(() => {
    if (!isInIframe) return;

    sendParentMessage(OUTGOING_MESSAGES.INITIALIZE);
  }, []);

  useEffect(() => {
    if (!redirectUrl) return;
    navigate(redirectUrl);
  }, [redirectUrl]);

  return null;
};

export default HostedPagePreview;
